import moment from 'moment';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { Button, Message } from '@bwoty-web/ui-kit';
import { useEffect, useState } from 'react';
import { useIsGlobetrotter, useIsMobile } from '@bwoty-web/ui-kit/hooks';

import bookingTypes from '../constants/bookingTypes';
import CompressedHotelSearch from '../components/compressedHotelSearch/compressedHotelSearch';
import DateField from '../components/date/DateField';
import DatePicker from '../components/date/DatePicker';
import DepartureField from '../components/departure/DepartureField';
import DepartureSelect from '../components/departure/DepartureSelect';
import DurationField from '../components/duration/DurationField';
import DurationSelect from '../components/duration/DurationSelect';
import HotelSearchPlaceholder from '../components/placeholders/hotelSearch';
import PaxField from '../components/pax/PaxField';
import PaxSelect from '../components/pax/PaxSelect';
import SelectOverlay from '../components/selectOverlay/SelectOverlay';
import { maxNumberOfPax } from '../constants/pax';
import { getDurationText } from '../utils/duration/helper';
import { validRoomDistribution } from '../utils/pax/validation';
import { getPriceCalendarDataKey } from '../utils/actionHelper';
import { getSearchUrl, getSearchUrlIdun } from '../services/hotelSearch';
import { ScrollElementToBottomOfScreen, ScrollToTop } from '../utils/scroll';
import { shiftFocusToPreviousSiblingIfKeyBoardWasUsed } from '../utils/keyboardHandler';
import {
  initHotelSearch,
  getInitState,
  changeDeparture,
  changeDuration,
  changeRoomDistribution,
  changeDates,
  setPreviousDuration,
  setShowPriceCalendar,
  setShowDirectFlights,
} from '../redux/actions/hotelSearch/hotelSearch';
import { distanceElementTopToDocumentTop } from '../utils/element';

import './bookingstart.scss';

const HotelSearch = (props) => {
  const {
    cookieId,
    departureDates,
    departures,
    dispatch,
    durations,
    hotelPartId,
    isEveryoneInSameRoomChecked,
    isIdun,
    isInitialized,
    isInspiration,
    isLoading,
    labels,
    previousDuration,
    priceCalendarData,
    priceCalendarDurationOptions,
    rooms,
    searchInfoMissing,
    selectedDeparture,
    selectedDepartureDate,
    selectedDuration,
    selectedHotelId,
    selectedNumberOfRooms,
    selectedReturnDate,
    showDirectFlights,
    showPriceCalendar,
    siteSettings,
  } = props;
  const isMobile = useIsMobile();
  const unspecifiedHotels = ['13281', '13198', '13207', '13214'];
  const [showDepartureLayer, toggleDepartureLayer] = useState(false);
  const [showDurationLayer, toggleDurationLayer] = useState(false);
  const [showDateLayer, toggleDateLayer] = useState(false);
  const [showPaxLayer, togglePaxLayer] = useState(false);
  const [dateSelectionInProgress, setDateSelectionInProgress] = useState(false);
  const [departureDateDuringSelection, setDepartureDateDuringSelection] = useState(null);
  const [returnDateDuringSelection, setReturnDateDuringSelection] = useState(null);
  const [isCompressed, setIsCompressed] = useState(!isInspiration);
  const [paxIsValid, setPaxIsValid] = useState(true);

  const isUnspecifiedHotel = unspecifiedHotels.includes(hotelPartId);
  const isGlobetrotter = useIsGlobetrotter();

  const [isSticky, setIsSticky] = useState(false);

  const handleScroll = () => {
    const hotelSearchElement = document.getElementById('hotel-search-app-root');
    let expandedHotelSearchHeight = 0;

    if (!isCompressed) {
      expandedHotelSearchHeight = hotelSearchElement.clientHeight - 70;
    }

    const scrollY = window.scrollY > 0 ? window.scrollY : document.documentElement.scrollTop;

    if (
      scrollY >
      distanceElementTopToDocumentTop(hotelSearchElement) + expandedHotelSearchHeight - 12
    ) {
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', () => handleScroll(), false);

    return () => {
      window.addEventListener('scroll', () => handleScroll(), false);
    };
  }, [isCompressed]);

  useEffect(() => {
    dispatch(initHotelSearch(siteSettings, hotelPartId));
  }, []);

  useEffect(() => {
    dispatch(getInitState(siteSettings, cookieId));
  }, [isInitialized]);

  const showDurationField = () => {
    if (!durations) {
      return true;
    }

    const numberOfDurationTypes = durations.length;
    return (
      numberOfDurationTypes > 1 || (numberOfDurationTypes === 1 && !durations[0].flexibleDuration)
    );
  };

  const getDateRange = (bookingType) => {
    const independentRange = {
      min: moment().add(siteSettings.datePicker.dpBookDaysAhead, 'days'),
      max: moment().add(siteSettings.datePicker.dpMaxDaysToReturn, 'days'),
    };

    const charterRange = {
      min: moment(),
      max: moment().add(19, 'month').endOf('month'),
    };

    return bookingType === bookingTypes.dp ? independentRange : charterRange;
  };

  const isSelectOverlayOpen = () => {
    return showDepartureLayer || showDurationLayer || showPaxLayer || showDateLayer;
  };

  const handleDateLayer = () => {
    if (showDateLayer && returnDateDuringSelection == null) {
      setDateSelectionInProgress(false);
      setDepartureDateDuringSelection(null);
    }
    toggleDateLayer(!showDateLayer);
  };

  const handlePaxLayer = (paxValidation = true) => {
    setPaxIsValid(paxValidation);
    togglePaxLayer(!showPaxLayer);

    if (isMobile) {
      const selector = 'bs-form__search-button';
      ScrollElementToBottomOfScreen(document.getElementsByClassName(selector)[0]);
    }
  };

  const changeDurationForPriceCalendar = (priceCalendarDuration) => {
    const priceCalendarDurationIncludedInDurations = durations.find(
      (duration) =>
        !duration.flexibleDuration &&
        duration.defaultDuration === priceCalendarDuration.defaultDuration,
    );
    const durationToSelect = priceCalendarDurationIncludedInDurations || priceCalendarDuration;

    dispatch(setPreviousDuration(selectedDuration));
    dispatch(changeDuration(durationToSelect, false));
  };

  const getCurrentPriceCalendarData = () => {
    const shouldShowPrices = showPriceCalendar && selectedDuration.hasCalendar;
    if (!shouldShowPrices) {
      return [];
    }
    const key = getPriceCalendarDataKey(
      selectedDeparture.itemId,
      0,
      selectedHotelId,
      selectedDuration,
      showDirectFlights,
    );
    return priceCalendarData[key];
  };

  const navigateToResultPage = async () => {
    const url = isIdun ? await getSearchUrlIdun(props) : await getSearchUrl(props);
    if (window.location.pathname + window.location.search === url.split('#')[0]) {
      window.location.reload();
    } else {
      window.location.href = url;
    }
  };

  const searchPackage = () => {
    if (showPaxLayer) {
      // Wait to the pax layer is closed
      setTimeout(() => {
        if (validRoomDistribution(rooms)) {
          navigateToResultPage();
        } else {
          handlePaxLayer(false);
        }
      }, 10);
    } else if (validRoomDistribution(rooms)) {
      navigateToResultPage();
    } else {
      handlePaxLayer(false);
    }
  };

  const expandHotelSearchControl = () => {
    setIsCompressed(false);
    if (isMobile) {
      ScrollToTop(document.getElementById('hotel-search-app-root'));
    }
  };

  if (isUnspecifiedHotel || !isInitialized) {
    return null;
  }

  const {
    travelTypeInformationMissingText,
    hotelSearchInspirationHeading,
    departureLayerHeading,
    durationLayerHeading,
    searchButtonText,
  } = labels;

  if (isLoading) {
    return <HotelSearchPlaceholder />;
  }

  const hotelSearchControlClassNames = classNames('bookingstart', 'hotel-search', {
    'hotel-search--no-duration-field': !showDurationField(),
    'hotel-search--compressed': isCompressed,
    'hotel-search--sticky': isSticky && !isInspiration && isMobile && isCompressed,
  });

  if (searchInfoMissing) {
    return (
      <div>
        {isInspiration && <Message header={travelTypeInformationMissingText} messageType="info" />}
      </div>
    );
  }

  const isCharter = selectedDuration.bookingType === bookingTypes.charter;

  return (
    <div className={hotelSearchControlClassNames}>
      {isInspiration && !isGlobetrotter && (
        <div className="bs-top-container">
          <div className="bs-heading">{hotelSearchInspirationHeading}</div>
        </div>
      )}
      <div className="bs-form">
        <DepartureField
          isActive={showDepartureLayer}
          isLoading={isLoading}
          isOtherFieldActive={!showDepartureLayer && isSelectOverlayOpen()}
          labels={labels}
          selectedAirport={selectedDeparture}
          toggleOverlay={() => toggleDepartureLayer(!showDepartureLayer)}
        >
          {!isLoading && (
            <SelectOverlay
              close={() => toggleDepartureLayer(false)}
              heading={departureLayerHeading}
            >
              <DepartureSelect
                airportList={departures}
                changeAirport={(e, departure) => {
                  shiftFocusToPreviousSiblingIfKeyBoardWasUsed(e);
                  toggleDepartureLayer(false);
                  dispatch(changeDeparture(departure));
                }}
                heading={departureLayerHeading}
                selectedAirport={selectedDeparture}
              />
            </SelectOverlay>
          )}
        </DepartureField>

        <DurationField
          isActive={showDurationLayer}
          isLoading={isLoading}
          isOtherFieldActive={!showDurationLayer && isSelectOverlayOpen()}
          labels={labels}
          selectedDuration={selectedDuration}
          showDurationField={showDurationField()}
          toggleOverlay={() => toggleDurationLayer(!showDurationLayer)}
        >
          {!isLoading && (
            <SelectOverlay heading={durationLayerHeading} close={() => toggleDurationLayer(false)}>
              <DurationSelect
                changeDuration={(e, duration) => {
                  shiftFocusToPreviousSiblingIfKeyBoardWasUsed(e);
                  toggleDurationLayer(false);
                  dispatch(changeDuration(duration));
                }}
                durationList={durations}
                labels={labels}
                selectedDuration={selectedDuration}
              />
            </SelectOverlay>
          )}
        </DurationField>

        <DateField
          datePickerIsActive={showDateLayer}
          dateSelectionInProgress={dateSelectionInProgress}
          dateSettings={siteSettings.datePicker}
          departureDateDuringSelection={departureDateDuringSelection}
          isActive={showDateLayer}
          isLoading={isLoading}
          isOtherFieldActive={!showDateLayer && isSelectOverlayOpen()}
          labels={labels}
          returnDateDuringSelection={returnDateDuringSelection}
          selectedDepartureDate={selectedDepartureDate}
          selectedDuration={selectedDuration}
          selectedReturnDate={selectedReturnDate}
          setDateSelectionInProgress={(inProgress) => setDateSelectionInProgress(inProgress)}
          showDateField
          toggleOverlay={() => handleDateLayer()}
        >
          {!isLoading && showDateLayer && (
            <DatePicker
              changeDate={(departureDate, returnDate) => {
                handleDateLayer();
                dispatch(changeDates(departureDate, returnDate));
              }}
              changeDuration={(duration) => {
                dispatch(changeDuration(duration));
              }}
              changeDurationForPriceCalendar={changeDurationForPriceCalendar}
              close={() => handleDateLayer()}
              dateSettings={siteSettings.datePicker}
              labels={labels}
              previousDuration={previousDuration}
              priceCalendarData={getCurrentPriceCalendarData()}
              priceCalendarDurationOptions={priceCalendarDurationOptions}
              range={getDateRange(selectedDuration.bookingType)}
              selectableDepartureDates={departureDates}
              selectedDepartureDate={selectedDepartureDate}
              selectedDuration={selectedDuration}
              selectedReturnDate={selectedReturnDate}
              setDateSelectionInProgress={(inProgress) => setDateSelectionInProgress(inProgress)}
              setDepartureDateDuringSelection={(date) => setDepartureDateDuringSelection(date)}
              setReturnDateDuringSelection={(date) => setReturnDateDuringSelection(date)}
              setShowDirectFlights={(show) => dispatch(setShowDirectFlights(show))}
              setShowPriceCalendar={(show) => dispatch(setShowPriceCalendar(show))}
              showDirectFlights={showDirectFlights}
              showPriceCalendar={showPriceCalendar}
              siteId={siteSettings.siteId}
            />
          )}
        </DateField>

        <PaxField
          isActive={showPaxLayer}
          isLoading={isLoading}
          isOtherFieldActive={!showPaxLayer && isSelectOverlayOpen()}
          labels={labels}
          roomDistribution={rooms}
          toggleOverlay={() => togglePaxLayer(!showPaxLayer)}
        >
          {!isLoading && (
            <PaxSelect
              changeRoomDistribution={(e, roomDistribution, numberOfRooms) => {
                shiftFocusToPreviousSiblingIfKeyBoardWasUsed(e);
                togglePaxLayer(false);
                dispatch(changeRoomDistribution(roomDistribution, numberOfRooms));
              }}
              close={() => togglePaxLayer(false)}
              hideSameRoomCheckbox
              isSameRoom={isEveryoneInSameRoomChecked}
              labels={labels}
              maxNumberOfPax={isCharter ? maxNumberOfPax.charter : maxNumberOfPax.dp}
              multipleRooms={selectedDuration.bookingType === bookingTypes.dp}
              numberOfRooms={selectedNumberOfRooms}
              paxIsValid={paxIsValid}
              roomDistribution={rooms}
            />
          )}
        </PaxField>

        <div className="bs-form__button-container">
          <Button
            className="bs-form__search-button"
            disabled={isLoading}
            onClick={() => searchPackage()}
            rel="nofollow"
            variant="primary"
          >
            {searchButtonText}
          </Button>
        </div>
      </div>

      {!isInspiration && isCompressed && (
        <CompressedHotelSearch
          isLoading={isLoading}
          labels={labels}
          departure={selectedDeparture}
          duration={getDurationText(
            selectedDuration,
            selectedDepartureDate,
            selectedReturnDate,
            labels,
          )}
          rooms={rooms}
          expand={() => expandHotelSearchControl()}
        />
      )}
    </div>
  );
};

HotelSearch.propTypes = {
  hotelPartId: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  isInitialized: PropTypes.bool.isRequired,
  departures: PropTypes.arrayOf(PropTypes.shape({})),
  selectedDeparture: PropTypes.shape({
    itemId: PropTypes.number,
  }),
  selectedHotelId: PropTypes.number,
  durations: PropTypes.arrayOf(
    PropTypes.shape({
      bookingType: PropTypes.string,
      name: PropTypes.string,
      flexibleDuration: PropTypes.bool,
    }),
  ),
  selectedDuration: PropTypes.shape({
    bookingType: PropTypes.string,
    name: PropTypes.string,
    flexibleDuration: PropTypes.bool,
    hasCalendar: PropTypes.bool,
  }),
  departureDates: PropTypes.arrayOf(PropTypes.string),
  selectedDepartureDate: PropTypes.string,
  selectedReturnDate: PropTypes.string,
  rooms: PropTypes.arrayOf(
    PropTypes.shape({
      numberOfAdults: PropTypes.number,
      numberOfChildren: PropTypes.number,
      childAges: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  selectedNumberOfRooms: PropTypes.number,
  isEveryoneInSameRoomChecked: PropTypes.bool,
  searchInfoMissing: PropTypes.bool,
  isInspiration: PropTypes.bool,
  isIdun: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  siteSettings: PropTypes.shape({
    datePicker: PropTypes.shape({
      dpBookDaysAhead: PropTypes.number,
      dpMaxDaysToReturn: PropTypes.number,
    }),
    siteId: PropTypes.number,
  }).isRequired,
  labels: PropTypes.shape({
    travelTypeInformationMissingText: PropTypes.string,
    hotelSearchInspirationHeading: PropTypes.string,
    departureLayerHeading: PropTypes.string,
    durationLayerHeading: PropTypes.string,
    searchButtonText: PropTypes.string,
  }),
  showPriceCalendar: PropTypes.bool,
  showDirectFlights: PropTypes.bool,
  priceCalendarData: PropTypes.shape(),
  priceCalendarDurationOptions: PropTypes.arrayOf(PropTypes.shape()),
  previousDuration: PropTypes.shape(),
  cookieId: PropTypes.string,
};

HotelSearch.defaultProps = {
  cookieId: '-',
  departureDates: undefined,
  departures: undefined,
  durations: undefined,
  isEveryoneInSameRoomChecked: undefined,
  isInspiration: false,
  isLoading: undefined,
  labels: undefined,
  previousDuration: null,
  priceCalendarData: undefined,
  priceCalendarDurationOptions: undefined,
  rooms: undefined,
  searchInfoMissing: undefined,
  selectedDeparture: undefined,
  selectedDepartureDate: undefined,
  selectedDuration: undefined,
  selectedHotelId: undefined,
  selectedNumberOfRooms: undefined,
  selectedReturnDate: undefined,
  showDirectFlights: undefined,
  showPriceCalendar: undefined,
};

const select = (state) => {
  return {
    labels: state.labels,
    searchInfoMissing: state.searchInfoMissing,
    isInitialized: state.isInitialized,
    isLoading: state.isLoading,
    selectedHotelId: state.selectedHotelId,
    selectedResortId: state.selectedResortId,
    departures: state.departures,
    selectedDeparture: state.selectedDeparture,
    durations: state.durations,
    selectedDuration: state.selectedDuration,
    departureDates: state.departureDates,
    selectedDepartureDate: state.selectedDepartureDate,
    selectedReturnDate: state.selectedReturnDate,
    rooms: state.rooms,
    selectedNumberOfRooms: state.selectedNumberOfRooms,
    showPriceCalendar: state.showPriceCalendar,
    showDirectFlights: state.showDirectFlights,
    priceCalendarDurationOptions: state.priceCalendarDurationOptions,
    priceCalendarData: state.priceCalendarData,
    previousDuration: state.previousDuration,
  };
};

export default connect(select)(HotelSearch);
